import { AvatarImage } from '../../../components/avatar-image'
import { useLocalMediaPreview } from '../hooks/use-local-media-preview'
import type { CallParticipant } from '../../../types/voice-call'

function MicIcon({ muted }: { muted: boolean }) {
  if (muted) {
    return (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="M1 1l22 22M9 9v3a3 3 0 005.12 2.12M15 9.34V5a3 3 0 00-5.94-.6" />
        <path d="M12 19v3M8 22h8M12 15a3 3 0 01-3-3V5a3 3 0 016 0v7" />
      </svg>
    )
  }
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 1a3 3 0 00-3 3v7a3 3 0 006 0V4a3 3 0 00-3-3z" />
      <path d="M19 10v1a7 7 0 01-14 0v-1M12 19v3M8 22h8" />
    </svg>
  )
}

function CameraIcon({ off }: { off: boolean }) {
  if (off) {
    return (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="M1 1l22 22M5 7h3l2-2h4l2 2h4a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V9a2 2 0 012-2z" />
      </svg>
    )
  }
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  )
}

type Props = {
  mode: 'host' | 'guest'
  title: string
  subtitle: string
  participants: CallParticipant[]
  displayName: string
  avatarUrl?: string | null
  isMuted: boolean
  isCameraOff: boolean
  onToggleMute: () => void
  onToggleCamera: () => void
  onJoin: () => void
  onCancel: () => void
  joinLabel?: string
  cancelLabel?: string
}

/**
 * Google Meet-style lobby: local camera preview + mic/camera toggles before
 * the user actually dials into the Chime meeting.
 */
export function GroupCallPrejoin({
  mode,
  title,
  subtitle,
  participants,
  displayName,
  avatarUrl,
  isMuted,
  isCameraOff,
  onToggleMute,
  onToggleCamera,
  onJoin,
  onCancel,
  joinLabel = 'Join now',
  cancelLabel,
}: Props) {
  const { videoRef } = useLocalMediaPreview({
    enabled: true,
    video: true,
    audio: true,
    muted: isMuted,
    cameraOff: isCameraOff,
  })

  const inviteCount = participants.filter((p) => p.state === 'ringing' || p.state === 'invited').length
  const resolvedCancel = cancelLabel ?? (mode === 'host' ? 'Cancel' : 'Decline')

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col bg-slate-950 native-modal-top native-modal-bottom"
      data-dark-surface="true"
      role="dialog"
      aria-modal="true"
      aria-label="Ready to join group video call"
    >
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-6 px-4 py-6 sm:flex-row sm:items-center sm:justify-center sm:gap-10 sm:px-8">
        {/* Preview pane */}
        <div className="w-full max-w-lg shrink-0">
          <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-slate-900 shadow-xl ring-1 ring-white/10">
            <video
              ref={videoRef}
              className={`absolute inset-0 h-full w-full object-cover ${isCameraOff ? 'opacity-0' : ''}`}
              autoPlay
              playsInline
              muted
              aria-label="Your camera preview"
            />
            {isCameraOff && (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-800">
                <AvatarImage
                  name={displayName}
                  avatarUrl={avatarUrl}
                  size={88}
                  variant="provider"
                  fallback="logo"
                />
              </div>
            )}
            <div className="absolute bottom-3 left-3 rounded-md bg-black/60 px-2 py-1 text-xs font-medium text-white">
              {displayName}
            </div>
          </div>

          <div className="mt-4 flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={onToggleMute}
              className={`flex h-11 w-11 items-center justify-center rounded-full text-white shadow-lg transition ${
                isMuted ? 'bg-red-500/90 hover:bg-red-600' : 'bg-white/15 hover:bg-white/25'
              }`}
              aria-label={isMuted ? 'Unmute microphone' : 'Mute microphone'}
              aria-pressed={isMuted}
            >
              <MicIcon muted={isMuted} />
            </button>
            <button
              type="button"
              onClick={onToggleCamera}
              className={`flex h-11 w-11 items-center justify-center rounded-full text-white shadow-lg transition ${
                isCameraOff ? 'bg-red-500/90 hover:bg-red-600' : 'bg-white/15 hover:bg-white/25'
              }`}
              aria-label={isCameraOff ? 'Turn camera on' : 'Turn camera off'}
              aria-pressed={isCameraOff}
            >
              <CameraIcon off={isCameraOff} />
            </button>
          </div>
        </div>

        {/* Meeting details + actions */}
        <div className="flex w-full max-w-sm flex-col items-center text-center text-white sm:items-start sm:text-left">
          <h2 className="text-2xl font-bold tracking-tight">{title}</h2>
          <p className="mt-2 text-sm text-white/70">{subtitle}</p>
          {mode === 'host' && inviteCount > 0 && (
            <p className="mt-1 text-xs text-white/50">
              {inviteCount} {inviteCount === 1 ? 'person' : 'people'} will be rung when you join
            </p>
          )}

          <div className="mt-8 flex w-full flex-col gap-3 sm:flex-row sm:justify-start">
            <button
              type="button"
              onClick={onJoin}
              className="w-full rounded-full bg-brand-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-brand-700 sm:w-auto"
            >
              {joinLabel}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="w-full rounded-full border border-white/25 bg-transparent px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/10 sm:w-auto"
            >
              {resolvedCancel}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
